import React, { useCallback, useEffect, useState } from 'react';
import { showError } from '../../../../../utils/ecxSwal';
import { PatientActivitySkeleton } from '../subparts/PatientTabSkeletons';
import patientService from '../../../../../services/patient.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import Pagination from '../../../../common/Pagination';
import ActivityGroupedList from '../../../activity/subparts/ActivityGroupedList';
import ActivityDetailsModal from '../../../activity/subparts/ActivityDetailsModal';
import { formatPatientCount } from '../../subparts/patientFilesQuery';
import PatientDetailIcon from '../subparts/PatientDetailIcon';
import '../../../activity/Activity.css';

const PAGE_SIZE = 20;

export default function PatientActivity({ patientId }) {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const [loading, setLoading] = useState(true);
    const [activityList, setActivityList] = useState([]);
    const [pageNumber, setPageNumber] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const loadActivity = useCallback(
        async (page = 0) => {
            if (!clinicId || !patientId) {
                return;
            }

            try {
                setLoading(true);
                const res = await patientService.getPatientActivities(clinicId, patientId, page, PAGE_SIZE);
                const data = res.data || {};

                setActivityList(data.content || []);
                setPageNumber(data.number ?? page);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
            } catch (err) {
                console.error('Failed to load patient activity', err);
                showError({ title: 'Load Failed', text: 'Failed to load patient activity.' });
                setActivityList([]);
                setTotalPages(0);
                setTotalElements(0);
            } finally {
                setLoading(false);
            }
        },
        [clinicId, patientId]
    );

    useEffect(() => {
        loadActivity(0);
    }, [loadActivity]);

    const handlePageChange = (nextPage) => {
        loadActivity(nextPage);
    };

    if (loading && activityList.length === 0) {
        return <PatientActivitySkeleton />;
    }

    const showPagination = !loading && activityList.length > 0 && totalPages > 1;

    return (
        <div className="patient-detail-tab-panel patient-detail-tab-panel--activity">
            <div className="patient-detail-tab-panel__header">
                <div>
                    <h3 className="patient-detail-tab-panel__title">
                        <PatientDetailIcon icon="bi-activity" theme="violet" size="sm" />
                        Patient Activity
                        {totalElements > 0 && (
                            <span className="ecx-page-chip ms-2">{formatPatientCount(totalElements)}</span>
                        )}
                    </h3>
                    <p className="patient-detail-tab-panel__subtitle">
                        Communication history and activity log for this patient.
                    </p>
                </div>
            </div>

            <div className="patient-detail-activity-body">
                {loading ? (
                    <div className="patient-detail-activity-scroll ecx-page-scroll">
                        <PatientActivitySkeleton compact={activityList.length > 0} />
                    </div>
                ) : activityList.length === 0 ? (
                    <div className="patient-detail-empty-state">
                        <PatientDetailIcon icon="bi-inbox" theme="slate" size="lg" />
                        <p>No activity found for this patient.</p>
                    </div>
                ) : (
                    <div className="patient-detail-activity-scroll ecx-page-scroll">
                        <ActivityGroupedList
                            records={activityList}
                            onViewDetails={setSelectedActivity}
                        />
                    </div>
                )}

                {showPagination && (
                    <div className="patient-detail-pagination">
                        <Pagination
                            currentPage={pageNumber}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            pageSize={PAGE_SIZE}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>

            <ActivityDetailsModal
                show={Boolean(selectedActivity)}
                onClose={() => setSelectedActivity(null)}
                clinicId={clinicId}
                activityId={selectedActivity?.id}
                previewItem={selectedActivity}
            />
        </div>
    );
}
